// Tuning data (00-architecture.md §6). Values come from `unity-overview.md`,
// cited by section; later plans extend this file.

// Type-only import: erased at build time, so the defs <-> bugs cycle never
// exists at runtime.
import type { BugKind } from "./bugs";

/**
 * How long a block stays `infected` before it decays to `ghost`, seconds —
 * `unity-overview.md` §3 "Blocks, infection, repair".
 *
 * This is the original's *intent*, not its shipped behaviour. `InfectBlock`
 * passes `0.33f` into `Block.InfectionRate` and the countdown in
 * `BlocksModel.Update` was clearly meant to run from it, but `InfectBlock` sets
 * `NextInfect = 0` and the only code that would apply the rate —
 * `ResetBlockNextInfect` (`BlocksModel.cs:527-531`) — has **zero callers**. So
 * the shipped `Infected` stage lasts a single frame and the 0.33 is dead state.
 * Porting the defect verbatim would make infection invisible and destroy a
 * block on contact; 0.33 s is what the code says it was for.
 */
export const INFECTED_SECONDS = 0.33;

/**
 * Tiles a bug walks between two infect stops, drawn per bite — the original's
 * `DistanceLeft = Random.Range(1, 20)` (`BugsModel.cs:1927`), counted in tiles
 * rather than world units. Unity's integer `Random.Range` has an **exclusive**
 * upper bound, so the draw is over 1..19 and averages 10.
 *
 * Plan 07 used a fixed 3 for determinism. That is no longer needed — the sim
 * already carries a seeded `Rng` (00-architecture.md §3) — and it stopped being
 * safe once a single touch destroys a block: at a bite every 3 tiles, twelve
 * bugs strip the surface in well under a minute.
 */
export const INFECT_TILES_MIN = 1;
export const INFECT_TILES_MAX = 20;

/**
 * How long a bug stands still to eat, seconds. This MUST match the cooked
 * `infect` clip so the animation neither loops nor freezes: every bug's clip is
 * 25 frames @ 30 fps = 0.8 s (the black bug's is 20 frames — the renderer
 * stretches each model's own clip over this duration, `bug-renderer.ts`).
 * Plan 07 quotes 0.833 s; the cooked duration is `(frames - 1) / fps = 0.8`.
 */
export const BITE_SECONDS = 0.8;

/**
 * Per-kind bug tuning — `unity-overview.md` §5 "Bug definitions".
 *
 * `speed` is converted to this port's units: Unity speed 3 = 1 block/s =
 * 1 cell/s (§6 "Cooked-model conventions"), so speed = Unity value / 3.
 * `points` and `affectedByFreeze` are carried verbatim for plans 08+.
 */
export interface BugDef {
  /** Cells per second along the surface. */
  readonly speed: number;
  /** Score awarded on kill. */
  readonly points: number;
  /** Whether the Bug Freeze power-up slows this kind. */
  readonly affectedByFreeze: boolean;
}

export const BUG_DEFS = {
  standard: { speed: 3 / 3, points: 100, affectedByFreeze: true },
  lady: { speed: 3 / 3, points: 1000, affectedByFreeze: true },
  fat: { speed: 2 / 3, points: 800, affectedByFreeze: true },
  speed: { speed: 5 / 3, points: 200, affectedByFreeze: true },
  spy: { speed: 4 / 3, points: 500, affectedByFreeze: true },
  spike: { speed: 4 / 3, points: 500, affectedByFreeze: true },
  black: { speed: 4 / 3, points: 0, affectedByFreeze: false },
} as const satisfies Readonly<Record<BugKind, BugDef>>;

/**
 * Grow-in duration of a freshly spawned bug, seconds.
 * APPROX: the Unity original scales the bug in over the spawn point's
 * animation rather than a serialized constant; 1 s matches its pacing.
 */
export const SPAWN_SECONDS = 1;

/**
 * Half a walker's body length, in cells — how far ahead of its centre a bug
 * reaches when claiming the tile it is entering (`occupancy.ts`).
 * APPROX: the Unity original occupies whole blocks rather than measuring the
 * mesh; slightly over half a cell keeps a walker's claim on the tile it is
 * leaving until its centre is safely past the boundary.
 */
export const HALF_BODY = 0.55;

/**
 * Walking acceleration, cells/s². The original ramps `CurrentSpeed` toward
 * `MaxSpeed` at `Time.deltaTime * 10` (`BugsModel.Accelerate`) in Unity speed
 * units, where 3 = 1 cell/s — so 10/3 here. Bugs spawn at a standstill and the
 * same rate brakes them, which is what makes an approach read as a slowdown
 * rather than a stop-frame.
 */
export const ACCELERATION = 10 / 3;

/**
 * Turn rate in degrees per second per cell/s of current speed — the original's
 * `EdgeWalkAngle += TargetTurnAngle * 0.45 * CurrentSpeed * dt` with a 90°
 * target and Unity speed 3 = 1 cell/s: 90 · 0.45 · 3. A standard bug therefore
 * sweeps its quarter turn in ~0.74 s. The arc is a quarter circle of radius
 * half a cell (length 0.785 cells), so the bug covers it ~6% faster than its
 * walking speed — close enough that the run cycle does not visibly slip.
 */
export const TURN_DEGREES_PER_SPEED = 90 * 0.45 * 3;

/**
 * How long a bug may sit blocked before it gives up and dies, seconds. The
 * original culls a walker stuck in `WaitingForWalking` after
 * `3 + (Id % 10) / 2` s (`BugsModel.cs`, the `WaitingForWalking` case) — the
 * deadlock breaker for walkers that can neither pass nor turn aside. The id
 * term staggers simultaneous give-ups; it is kept for the same reason.
 */
export const BLOCKED_SECONDS_BASE = 3;

/** Below this speed a walker counts as stopped (cells/s). */
export const STOPPED_SPEED = 0.01;

/**
 * Death animation duration, seconds — the cooked `die` clip of
 * `0_bug_standard` (55 frames @ 30 fps). The bug is removed when it elapses.
 */
export const DIE_SECONDS = 1.8;

// ---------------------------------------------------------------------------
// Spawn points — the apples (plan 06)
// ---------------------------------------------------------------------------

/**
 * The seven bug kinds in `BugsDefinitionInspector` order — the order the level
 * table's occurrence weights are given in (`unity-overview.md` §5 "Bug
 * definitions" / "Levels"). Index is the original's `DefinitionType`.
 */
export const BUG_KIND_ORDER = [
  "standard",
  "lady",
  "fat",
  "speed",
  "spy",
  "spike",
  "black",
] as const satisfies readonly BugKind[];

/** One row of the 30-entry level table (`unity-overview.md` §5 "Levels"). */
export interface LevelDef {
  /**
   * `ActiveAfterSeconds`. The level it introduces actually begins **one second
   * later** — the original's clock truncates elapsed time to whole seconds and
   * compares strictly (`Cube.cs:3228`), so see `LevelClock` in `levels.ts`.
   */
  readonly activeAfterSeconds: number;
  /** Percent weights in `BUG_KIND_ORDER`; may sum to < 100 (= "no spawn"). */
  readonly occurrenceWeights: readonly number[];
  /** `MaxAmountOfBugs` — spawning is denied at or above this population. */
  readonly maxBugs: number;
  /** `MeanTimeBetweenBugSpawn`, seconds. */
  readonly meanTimeBetweenBugSpawn: number;
  /** `AmountOfSpawnPoints` — 2 for every shipped level. */
  readonly spawnPoints: number;
  /**
   * `SpeedFactor` — a time dilation on every walker, not a per-kind speed. In
   * the original it multiplies the *distance moved this frame*; this port
   * scales the top speed instead (see `DIVERGENCES.md` → Corrections).
   */
  readonly speedFactor: number;
  /** `MeanTimeBetweenPowerUpCreation`, seconds (plan 16). */
  readonly meanTimeBetweenPowerUpCreation: number;
  /**
   * `PowerUpOccurenceInterval` — percent weights in `POWERUP_DEFS` slot order.
   * Every row sums to exactly 100 (`tests/powerups.test.ts` asserts it), which
   * is what makes the original's "wrong powerup settings" branch dead code.
   */
  readonly powerUpWeights: readonly number[];
}

/**
 * The whole 30-row difficulty ramp, transcribed from `unity-overview.md` §5
 * "Levels" (`LevelAfterSeconds` in `MainGame.unity`). Weights are percentages
 * in `BUG_KIND_ORDER` and may sum to < 100 — the remainder is "no spawn" for
 * that roll. `tests/levels.test.ts` guards the transcription.
 */
export const LEVELS = [
  { activeAfterSeconds: 1, occurrenceWeights: [100, 0, 0, 0, 0, 0, 0], maxBugs: 12, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.0, meanTimeBetweenPowerUpCreation: 3, powerUpWeights: [0, 0, 0, 0, 0, 0, 0, 0, 100, 0] },
  { activeAfterSeconds: 30, occurrenceWeights: [70, 0, 30, 0, 0, 0, 0], maxBugs: 12, meanTimeBetweenBugSpawn: 4, spawnPoints: 2, speedFactor: 1.01, meanTimeBetweenPowerUpCreation: 3, powerUpWeights: [0, 0, 0, 0, 0, 0, 0, 0, 100, 0] },
  { activeAfterSeconds: 60, occurrenceWeights: [40, 15, 35, 0, 0, 0, 10], maxBugs: 12, meanTimeBetweenBugSpawn: 3.5, spawnPoints: 2, speedFactor: 1.02, meanTimeBetweenPowerUpCreation: 3, powerUpWeights: [5, 5, 0, 0, 0, 0, 0, 0, 40, 50] },
  { activeAfterSeconds: 90, occurrenceWeights: [30, 10, 25, 25, 0, 0, 10], maxBugs: 12, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.0, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [5, 10, 0, 0, 0, 0, 0, 0, 45, 40] },
  { activeAfterSeconds: 120, occurrenceWeights: [20, 10, 15, 15, 15, 15, 10], maxBugs: 13, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.03, meanTimeBetweenPowerUpCreation: 3, powerUpWeights: [0, 0, 0, 0, 20, 0, 0, 0, 40, 40] },
  { activeAfterSeconds: 150, occurrenceWeights: [0, 85, 0, 0, 0, 0, 15], maxBugs: 13, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.04, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [30, 30, 0, 0, 0, 0, 0, 0, 20, 20] },
  { activeAfterSeconds: 180, occurrenceWeights: [30, 20, 0, 20, 15, 0, 15], maxBugs: 13, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.05, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [10, 10, 10, 10, 0, 0, 0, 20, 20, 20] },
  { activeAfterSeconds: 210, occurrenceWeights: [0, 15, 35, 25, 10, 0, 15], maxBugs: 13, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.06, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [10, 0, 10, 0, 10, 0, 10, 20, 20, 20] },
  { activeAfterSeconds: 240, occurrenceWeights: [5, 10, 30, 0, 20, 20, 15], maxBugs: 13, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.07, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [10, 10, 10, 0, 0, 0, 10, 20, 20, 20] },
  { activeAfterSeconds: 270, occurrenceWeights: [0, 10, 0, 0, 75, 0, 15], maxBugs: 13, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.08, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [20, 10, 0, 10, 0, 0, 10, 20, 10, 20] },
  { activeAfterSeconds: 300, occurrenceWeights: [0, 60, 0, 25, 0, 0, 15], maxBugs: 13, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.09, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [30, 30, 0, 0, 0, 0, 0, 0, 20, 20] },
  { activeAfterSeconds: 330, occurrenceWeights: [0, 10, 0, 0, 0, 70, 20], maxBugs: 13, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.1, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [0, 10, 0, 10, 0, 0, 10, 20, 30, 20] },
  { activeAfterSeconds: 360, occurrenceWeights: [5, 10, 15, 15, 15, 20, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.11, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [0, 5, 10, 0, 0, 5, 10, 20, 30, 20] },
  { activeAfterSeconds: 390, occurrenceWeights: [10, 10, 15, 15, 20, 15, 15], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.12, meanTimeBetweenPowerUpCreation: 3, powerUpWeights: [20, 0, 0, 10, 0, 5, 20, 0, 25, 20] },
  { activeAfterSeconds: 420, occurrenceWeights: [0, 10, 15, 15, 20, 20, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.13, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [0, 10, 10, 10, 10, 5, 10, 10, 20, 15] },
  { activeAfterSeconds: 450, occurrenceWeights: [0, 80, 0, 0, 0, 0, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.14, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [30, 30, 0, 0, 0, 0, 0, 0, 20, 20] },
  { activeAfterSeconds: 480, occurrenceWeights: [10, 5, 10, 15, 25, 25, 10], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.08, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [10, 10, 10, 0, 10, 5, 10, 10, 20, 15] },
  { activeAfterSeconds: 510, occurrenceWeights: [0, 5, 25, 10, 20, 20, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.15, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [10, 5, 0, 20, 10, 5, 10, 10, 20, 10] },
  { activeAfterSeconds: 540, occurrenceWeights: [5, 5, 20, 15, 20, 20, 15], maxBugs: 14, meanTimeBetweenBugSpawn: 2, spawnPoints: 2, speedFactor: 1.16, meanTimeBetweenPowerUpCreation: 3, powerUpWeights: [5, 10, 10, 20, 10, 5, 10, 10, 10, 10] },
  { activeAfterSeconds: 570, occurrenceWeights: [5, 10, 15, 15, 20, 20, 15], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.16, meanTimeBetweenPowerUpCreation: 3, powerUpWeights: [0, 10, 10, 20, 10, 5, 10, 10, 15, 10] },
  { activeAfterSeconds: 600, occurrenceWeights: [0, 10, 30, 30, 10, 0, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.16, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [0, 10, 10, 20, 10, 5, 10, 10, 15, 10] },
  { activeAfterSeconds: 630, occurrenceWeights: [0, 10, 0, 0, 40, 30, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.12, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [0, 10, 10, 10, 10, 5, 10, 10, 25, 10] },
  { activeAfterSeconds: 660, occurrenceWeights: [10, 5, 30, 30, 0, 0, 25], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.12, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [0, 10, 10, 10, 10, 5, 10, 10, 25, 10] },
  { activeAfterSeconds: 690, occurrenceWeights: [30, 10, 0, 0, 10, 30, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.12, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [5, 10, 10, 10, 10, 5, 5, 10, 25, 10] },
  { activeAfterSeconds: 720, occurrenceWeights: [10, 10, 30, 0, 30, 0, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.12, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [5, 10, 10, 10, 10, 5, 5, 10, 25, 10] },
  { activeAfterSeconds: 750, occurrenceWeights: [0, 10, 0, 30, 0, 40, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.16, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [5, 10, 10, 10, 10, 5, 5, 10, 25, 10] },
  { activeAfterSeconds: 780, occurrenceWeights: [10, 10, 15, 10, 20, 15, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.16, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [5, 10, 10, 10, 10, 5, 5, 10, 25, 10] },
  { activeAfterSeconds: 810, occurrenceWeights: [5, 10, 20, 15, 15, 20, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.16, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [5, 10, 10, 10, 10, 5, 5, 10, 25, 10] },
  { activeAfterSeconds: 840, occurrenceWeights: [10, 10, 15, 15, 15, 20, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 2, spawnPoints: 2, speedFactor: 1.16, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [5, 10, 10, 10, 10, 5, 5, 10, 25, 10] },
  { activeAfterSeconds: 870, occurrenceWeights: [10, 10, 10, 10, 10, 20, 30], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.16, meanTimeBetweenPowerUpCreation: 5, powerUpWeights: [5, 10, 10, 10, 10, 5, 5, 10, 25, 10] },
] as const satisfies readonly LevelDef[];

/**
 * Row 1. Kept as a named export because plan 06's tests and the spawner docs
 * speak of "level 1"; it is `LEVELS[0]` and nothing else.
 */
export const LEVEL_1 = LEVELS[0];

/**
 * The level whose expiry wins the run — `GameWonAfterLevel[0]` = 10 at
 * difficulty 0 (`MainGame.unity:3980`), i.e. the crossing at t = 301 s.
 * Easy's value; the per-difficulty ones live in `DIFFICULTIES` (plan 17).
 */
export const GAME_WON_AFTER_LEVEL = 10;

// ---------------------------------------------------------------------------
// Difficulty & the menu's demo ramp (plan 17)
// ---------------------------------------------------------------------------

/**
 * One difficulty row — `DifficultyDescriptions` (`GlobalSpeedMultiplier`) and
 * `GameWonAfterLevel` (`0a 0f 14` = 10/15/20) in `MainGame.unity`. The
 * shipped *labels* differ from the internal enum: the difficulty page's
 * textures read NORMAL / BUGGY / BUGG HARD for easy / normal / hard (see
 * `model-catalog.ts`); `name` keeps the internal `GameConfiguration` naming.
 *
 * Each row also names a `DifficultySpawnType` (0/2/1 = MoveStopSpawnTurn /
 * Alternating / SpawnWhileMoving) — NOT ported, only MoveStopSpawnTurn exists
 * (plan 06); see `DIVERGENCES.md`.
 */
export interface DifficultyDef {
  readonly name: "easy" | "normal" | "hard";
  readonly speedMultiplier: number;
  /** The level whose window's expiry wins the run (`GameWonAfterLevel`). */
  readonly winAfterLevel: number;
}

export const DIFFICULTIES = [
  { name: "easy", speedMultiplier: 1.0, winAfterLevel: GAME_WON_AFTER_LEVEL },
  { name: "normal", speedMultiplier: 1.0, winAfterLevel: 15 },
  { name: "hard", speedMultiplier: 1.1, winAfterLevel: 20 },
] as const satisfies readonly DifficultyDef[];

/**
 * The 7-row demo ramp the main menu's unattended game runs on —
 * `LevelAfterSecondsDemoMode` (`MainGame.unity:3769`), decoded from the raw
 * blobs like `LEVELS`. Gentler mix, `maxBugs` 14, and every row shares
 * `spawnPoints 2 / meanTimeBetweenBugSpawn 3 / meanTimeBetweenPowerUpCreation
 * 4`. NOTE: the shipped `MainMenu.unity` scene never actually consumes this
 * list — its cube carries a degenerate one-row table and five hand-placed
 * bugs — so this is the authored-but-unwired data (see `DIVERGENCES.md`).
 */
export const DEMO_LEVELS = [
  { activeAfterSeconds: 1, occurrenceWeights: [100, 0, 0, 0, 0, 0, 0], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.0, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [0, 0, 0, 0, 0, 0, 0, 0, 50, 50] },
  { activeAfterSeconds: 30, occurrenceWeights: [60, 0, 40, 0, 0, 0, 0], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.0, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [0, 25, 0, 25, 0, 0, 0, 0, 25, 25] },
  { activeAfterSeconds: 60, occurrenceWeights: [40, 30, 30, 0, 0, 0, 0], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.05, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [0, 20, 0, 20, 20, 0, 0, 0, 20, 20] },
  { activeAfterSeconds: 90, occurrenceWeights: [30, 30, 30, 0, 0, 0, 10], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.05, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [5, 10, 5, 10, 10, 5, 10, 10, 20, 15] },
  { activeAfterSeconds: 120, occurrenceWeights: [20, 20, 20, 30, 0, 0, 10], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.1, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [5, 10, 5, 10, 10, 5, 10, 10, 20, 15] },
  { activeAfterSeconds: 150, occurrenceWeights: [0, 0, 30, 0, 30, 30, 10], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.1, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [5, 10, 5, 10, 10, 5, 10, 10, 20, 15] },
  { activeAfterSeconds: 180, occurrenceWeights: [0, 10, 0, 20, 25, 25, 20], maxBugs: 14, meanTimeBetweenBugSpawn: 3, spawnPoints: 2, speedFactor: 1.2, meanTimeBetweenPowerUpCreation: 4, powerUpWeights: [5, 10, 5, 10, 10, 5, 10, 10, 20, 15] },
] as const satisfies readonly LevelDef[];

/** `Cube.cs:2345` — the run is lost at or below this healthy fraction. */
export const LOSE_HEALTHY_FRACTION = 0.08;

/**
 * A `SpeedFactor` at or below this reads as 1 (`BugsModel.cs:57`). No shipped
 * row hits it; the guard is ported because it is free.
 */
export const MIN_SPEED_FACTOR = 0.01;

/**
 * Apple walking speed, cells/s — the spawn-point definition's Unity speed 3
 * (`unity-overview.md` §5 "Bug definitions", last line), converted like the bug
 * speeds. Unlike bugs, apples walk at a constant speed: the original brakes them
 * only to stop in a block centre, which this port gets for free by ending legs
 * on centres.
 */
export const SPAWNPOINT_SPEED = 3 / 3;

/**
 * Seconds an apple walks before it stops to spawn or turn.
 * APPROX: the original derives the leg from how far the apple could still cover
 * before its next spawn is due (`BugsModel.cs` ~1054, `distanceCoverableUntil
 * NextSpawn`), which needs the level-speed machinery that is out of scope here.
 * Two seconds at 1 cell/s is a two-cell leg, which reads the same on screen.
 */
export const MOVE_LEG_SECONDS = 2;

/**
 * Seconds an apple takes to pivot 90 degrees on the spot — the original's
 * `Rotating` state turns at `360 * 0.5` deg/s (`BugsModel.cs` ~1026), so a
 * quarter turn is exactly half a second.
 */
export const TURN_SECONDS = 0.5;

/**
 * Seconds an apple stays stopped while a bug grows out of it. Matches
 * `SPAWN_SECONDS` so the apple moves off exactly as the bug starts walking.
 * APPROX: the original holds `TimeLeftUntilStateTransition = 2` after spawning
 * (`BugsModel.cs` ~1153), but that covers its stop *and* its rotate; here the
 * turn has its own state and duration.
 */
export const SPAWN_ANIM_SECONDS = SPAWN_SECONDS;

// ---------------------------------------------------------------------------
// Power-ups — the plants (plan 16)
// ---------------------------------------------------------------------------

/**
 * The ten power-up models in `PowerUpDefinitionInspector` slot order
 * (`MainGame.unity:3312`) — the order the per-level power-up weights index.
 * Slots 8 and 9 are **not** a typo: the scene authors slot 8 with `flower_5`
 * (the 1000-point small bonus) and slot 9 with `flower_4` (the 5000-point big
 * one), their `Effect` ordinals swapped — see plan 16 §2. Behaviour keys off
 * the slot, so the swap is inert here, but the model order must match.
 */
export const POWERUP_NAMES = [
  "mushroom1", // 0: 50% bugs die
  "mushroom2", // 1: 30 s double spawn
  "mushroom3", // 2: 30 s half speed
  "mushroom4", // 3: 30 s double speed
  "mushroom5", // 4: 15 s bug freeze
  "flower1", //   5: complete repair
  "flower2", //   6: 7-block repair
  "flower3", //   7: 3-block repair
  "flower5", //   8: small point bonus (yes, _5 before _4)
  "flower4", //   9: big point bonus
] as const;

export type PowerUpName = (typeof POWERUP_NAMES)[number];

/**
 * What collecting a power-up does. Slot-indexed tags, NOT the original's
 * `Effects` enum ordinals — the scene authors slots 8 and 9 with their
 * ordinals swapped (`MainGame.unity:3361-3372`), which is inert there only
 * because both point-bonus cases are empty `break`s. Both bonus slots take
 * `"points"` here, so the swap is a non-issue by construction (plan 16 §2).
 */
export type PowerUpEffect =
  | "killHalf"
  | "doubleSpawn"
  | "halfSpeed"
  | "doubleSpeed"
  | "freeze"
  | "repairAll"
  | "repairSeven"
  | "repairThree"
  | "points";

export interface PowerUpDef {
  /** The model name in `MODEL_CATALOG`. */
  readonly model: PowerUpName;
  /** Awarded on collection, unmultiplied (`Cube.cs:1166-1169`). */
  readonly points: number;
  /** Slot-indexed effect tag — see `PowerUpEffect`. */
  readonly effect: PowerUpEffect;
}

/**
 * The ten slots in `PowerUpDefinitionInspector` order (`MainGame.unity:3312`),
 * the order `LevelDef.powerUpWeights` indexes. Points from `unity-overview.md`
 * §5 "Power-up definitions".
 */
export const POWERUP_DEFS = [
  { model: "mushroom1", points: 3000, effect: "killHalf" },
  { model: "mushroom2", points: 10000, effect: "doubleSpawn" },
  { model: "mushroom3", points: 1000, effect: "halfSpeed" },
  { model: "mushroom4", points: 10000, effect: "doubleSpeed" },
  { model: "mushroom5", points: 1000, effect: "freeze" },
  { model: "flower1", points: 10000, effect: "repairAll" },
  { model: "flower2", points: 5000, effect: "repairSeven" },
  { model: "flower3", points: 1000, effect: "repairThree" },
  { model: "flower5", points: 1000, effect: "points" }, // slot 8: small bonus
  { model: "flower4", points: 5000, effect: "points" }, // slot 9: big bonus
] as const satisfies readonly PowerUpDef[];

// The effect tuning is hard-coded in `TakePowerUp` (`BugsModel.cs:2950-2967`),
// not serialized in the scene.
/** Divides `TimeUntilNextSpawn` while double-spawn runs (`BugsModel.cs:2950`). */
export const POWERUP_SPAWN_RATE = 2;
export const POWERUP_SPEED_FAST = 2;
export const POWERUP_SPEED_SLOW = 0.5;
/** Duration of the spawn-rate and both speed effects, seconds. */
export const POWERUP_TIMED_SECONDS = 30;
export const POWERUP_FREEZE_SECONDS = 15;
/** `_nextPowerUpCreation = 10f` in the constructor (`BugsModel.cs:33`). */
export const FIRST_POWERUP_SECONDS = 10;
export const REPAIR_SEVEN = 7;
export const REPAIR_THREE = 3;

// Clip durations `(frames - 1) / fps` off the cooked models, like
// `DIE_SECONDS` — all ten plants share identical grow/pick/die frame counts
// (100/30/25 @ 30 fps), so one constant each is exact for every model.
/** The `grow` clip; a growing power-up is already collectable (plan 16 §2). */
export const POWERUP_GROW_SECONDS = 99 / 30;
/** The `pick` clip — tapped; removed when it elapses. */
export const POWERUP_PICK_SECONDS = 29 / 30;
/** The `die` clip — trampled by a bug; removed when it elapses. */
export const POWERUP_DIE_SECONDS = 24 / 30;

// ---------------------------------------------------------------------------
// Player interaction — killing, repairing, combos (plan 08)
// ---------------------------------------------------------------------------

/**
 * Taps the fat bug absorbs before it dies — `BugsModel.HurtBug` counts
 * `AmountOfHitsByPlayer` up to 3 for the Fat type only; every other kind dies
 * to the first tap.
 */
export const FAT_HITS = 3;

/**
 * Pickup animation duration, seconds — the lady's cooked `pick` clip is 30
 * frames @ 30 fps, so `(frames - 1) / fps`, derived exactly like `DIE_SECONDS`.
 * The renderer stretches each model's own clip over it (the black bug's is 36).
 */
export const PICK_SECONDS = 29 / 30;

/**
 * Score multiplier by how long the bug had been alive when it was tapped —
 * `unity-overview.md` §5 "Score multipliers" (`BugLifeTimeIndicators`). First
 * matching row wins; anything older scores ×1.
 */
export const LIFETIME_MULTIPLIERS = [
  { under: 3, multiplier: 3 },
  { under: 6, multiplier: 2 },
] as const satisfies readonly { readonly under: number; readonly multiplier: number }[];

// ---------------------------------------------------------------------------
// Kind-specific behaviour — the roster (plan 14)
// ---------------------------------------------------------------------------

/**
 * Distinct marked blocks a spy collects before it cashes them in —
 * `BugsModel.cs:1654` (`MarkedBlocks.Distinct(...).Count() >= 3`).
 */
export const SPY_MARKS_TO_INFECT = 3;

/**
 * Blocks a fat bug's bite accounts for, the one it stands on included —
 * `BugsModel.cs:1902-1922` starts `amountInfected` at 1 and stops the ring walk
 * at 3, so the star adds **at most two** more.
 */
export const FAT_STAR_INFECTS = 3;

/**
 * The fat bug's `eightStar` (`BugsModel.cs:1931`), as `(forward, left)` offsets
 * in cells: `x` multiplies the bug's heading and `y` its left perpendicular, so
 * the ring **rotates with the walker** rather than sitting on world axes.
 *
 * The diagonals are 1.4 rather than 1 in the original, deliberately overshooting
 * past the corner. Kept verbatim: half a cell of overshoot from a lane centre
 * (0.5 + 1.4 = 1.9) still bins into the same cell 1.0 would, so it is faithful
 * and harmless here.
 *
 * The set is symmetric under `y → -y`, so which way "left" turns only reorders
 * the traversal — and the traversal starts at a random index anyway. This port
 * therefore takes `left` as the face-local left turn without checking it against
 * Unity's left-handed `Vector3.Cross(Direction, Up)`.
 */
export const EIGHT_STAR = [
  [0, 1],
  [1.4, 1.4],
  [1, 0],
  [1.4, -1.4],
  [0, -1],
  [-1.4, -1.4],
  [-1, 0],
  [-1.4, 1.4],
] as const satisfies readonly (readonly [number, number])[];

/**
 * Seconds a black bug lives before it dies of its own accord —
 * `MainGame.unity:3975` `BlackBugDisappearsAfterSeconds`. It dies *properly*
 * (`KillBug(..., NotCounting, killedByPlayer: false)`, `BugsModel.cs:1239-1249`):
 * the full die clip, and no score.
 */
export const BLACK_DESPAWN_SECONDS = 20;

/**
 * Seconds a repair chain may pause before it counts as broken —
 * `BlockCombos.AddBlock`'s `timeSinceLast > 0.5f` (`unity-overview.md` §5).
 */
export const COMBO_WINDOW = 0.5;

/**
 * Points for a broken chain of length 2..7 — `ScoreForDiamonds`
 * (`unity-overview.md` §5 "Score multipliers"). Index is `length - 2`, clamped:
 * a longer chain simply scores the top entry.
 */
export const COMBO_SCORES = [1000, 2000, 5000, 10000, 20000, 50000] as const;
